import { TZDate } from '@date-fns/tz'
import {
  endOfMonth,
  format,
  getHours,
  getMilliseconds,
  getMinutes,
  getSeconds,
  isSameDay,
  isToday,
  isValid,
  parse,
  roundToNearestMinutes,
  set,
  setHours,
  setMinutes,
  subMinutes,
  transpose,
} from 'date-fns'
import clamp from 'lodash-es/clamp'
import padStart from 'lodash-es/padStart'

import { EMPTY_OBJECT } from '../constants/common'
import {
  DATE_API_DATA_FORMAT,
  DATE_DAY_MONTH_YEAR_TIME_FORMAT,
  DATE_FULL_FORMAT_FROM_TIME,
  DATE_HOURS_FORMAT,
  DATE_INPUT_REGEXP,
  DATE_ROUNDING_METHOD,
  DATE_SHORT_FORMAT,
  DAY_FIRST_HOUR,
  DAY_SECONDS,
  HOUR_FIRST_MINUTE,
  HOUR_SECONDS,
  MINUTE_FIRST_SECOND,
  MINUTE_SECONDS,
  SECOND_FIRST_MILLISECOND,
  TIME_INPUT_REGEXP,
  TIME_PICKER_STEP_MINUTES,
} from '../constants/date'

import { isValueNumberType } from './common'

function normalizeDatePart(value) {
  const datePartNumber = parseInt(value, 10)

  if (isNaN(datePartNumber)) {
    return null
  }

  return datePartNumber < 10 ? `0${datePartNumber}` : String(datePartNumber)
}

function getDurationParts(seconds) {
  const days = Math.floor(seconds / DAY_SECONDS)
  const hours = Math.floor((seconds % DAY_SECONDS) / HOUR_SECONDS)
  const minutes = Math.floor((seconds % HOUR_SECONDS) / MINUTE_SECONDS)

  return [
    days ? `${days}d` : null,
    hours ? `${hours}h` : null,
    minutes || (!days && !hours) ? `${minutes}m` : null,
  ].filter(Boolean)
}

export function createNowDateInUkTimezone(timeZone) {
  return TZDate.tz(timeZone)
}

export function transposeToUkTimezoneDate(date, timeZone) {
  if (!timeZone || !date) {
    return null
  }

  const T = TZDate.tz(timeZone)

  return transpose(new TZDate(date), T)
}

export function transposeToLocalTimezoneDate(date, timeZone) {
  return date ? transpose(new TZDate(date, timeZone), TZDate) : null
}

export function toUkTimezoneDate(date, timeZone) {
  if (!timeZone) {
    console.error('Using `toUkTimezoneDate` without timeZone is prohibited!')
  }

  return new TZDate(date, timeZone)
}

export function formatDateToApi(date, timeZone) {
  return date
    ? format(toUkTimezoneDate(date, timeZone), DATE_API_DATA_FORMAT)
    : null
}

export function formatDate(date, time, timeZone) {
  if (!date) {
    return undefined
  }

  let dataValue = date
  let hours = 0
  let minutes = 0

  if (time) {
    hours = getHours(time)
    minutes = getMinutes(time)
  }

  dataValue = setHours(dataValue, hours)
  dataValue = setMinutes(dataValue, minutes)

  return formatDateToApi(dataValue, timeZone)
}

export function getNearestExistingDay(date, timeZone) {
  const dateArr = date.split('/')
  const month = dateArr[1]
  const year = dateArr[2]

  const existDate = set(createNowDateInUkTimezone(timeZone), {
    date: 1,
    month: month - 1,
    year,
  })

  return endOfMonth(existDate)
}

export function formatDateRelativeToToday(date, locale, timeZone) {
  const ukDate = toUkTimezoneDate(date, timeZone)

  return format(
    ukDate,
    isToday(ukDate) ? DATE_HOURS_FORMAT : DATE_FULL_FORMAT_FROM_TIME,
    {
      locale,
    }
  )
}

export function formatDateDayMonthYearTime({ date, locale, timeZone }) {
  return format(
    toUkTimezoneDate(date, timeZone),
    DATE_DAY_MONTH_YEAR_TIME_FORMAT,
    {
      locale,
    }
  )
}

export function formatDurationSeconds(seconds) {
  return isValueNumberType(seconds) ? getDurationParts(seconds).join(' ') : null
}

export function getFormattedDate({
  maxDate,
  maxYear = Infinity,
  minDate,
  minYear = -Infinity,
  value,
}) {
  // regex: day (1–2 digits), sep, datetime (1–2 digits), sep, year (exactly 4 digits)
  const match = value.match(/^(\d{1,2})[.\-\s/](\d{1,2})[.\-\s/](\d{4})$/)

  if (!match) {
    return undefined
  }

  const [, dayStr, monthStr, yearStr] = match

  const yearNum =
    !minDate && !maxDate
      ? clamp(yearStr, minYear, maxYear)
      : normalizeDatePart(yearStr)

  const dateString = `${normalizeDatePart(dayStr)}/${normalizeDatePart(monthStr)}/${yearNum}`

  const parsedDate = parse(dateString, DATE_SHORT_FORMAT, new Date())

  if (isNaN(parsedDate)) {
    return undefined
  }

  if (minDate && parsedDate < minDate) {
    return format(minDate, DATE_SHORT_FORMAT)
  }

  if (maxDate && parsedDate > maxDate) {
    return format(maxDate, DATE_SHORT_FORMAT)
  }

  return format(parsedDate, DATE_SHORT_FORMAT)
}

/**
 * The {@link createDate} function creates a date in the given timeZone or local
 * date if there is no timeZone provided.
 *
 * This function accepts the same arguments as the `Date` constructor and works
 * in the same manner.
 *
 * @param {string | null} timeZone
 * @param args
 *
 * @returns {Date}
 */
export function createDate(timeZone, ...args) {
  return timeZone ? TZDate.tz(timeZone, ...args) : new Date(...args)
}

/**
 * @param {string | null} timeZone
 * @param {number} year
 * @param {number} month
 * @param {number} day
 *
 * @returns {Date}
 */
export function createDay(timeZone, year, month, day) {
  return createDate(timeZone, year, month, day)
}

/**
 * @param {string | null} timeZone
 * @param {number} year
 * @param {number} month
 * @param {number} day
 *
 * @returns {Date}
 */
export function createDayWithCurrentTime(timeZone, year, month, day) {
  const current = createDate(timeZone)

  return createDate(
    timeZone,
    year,
    month,
    day,
    getHours(current),
    getMinutes(current),
    getSeconds(current),
    getMilliseconds(current)
  )
}

/**
 * @param {string | null} timeZone
 * @param {number} year
 * @param {number} month
 * @param {number} day
 * @param {import('date-fns').NearestMinutes | undefined} nearestTo
 *
 * @returns {Date | import("date-fns").TZDate}
 */
export function createDayWithNearestAfterCurrentTime(
  timeZone,
  year,
  month,
  day,
  nearestTo = TIME_PICKER_STEP_MINUTES
) {
  const dayWithCurrentTime = createDayWithCurrentTime(
    timeZone,
    year,
    month,
    day
  )

  const rounded = roundToNearestMinutes(dayWithCurrentTime, {
    nearestTo,
    roundingMethod: DATE_ROUNDING_METHOD.CEIL,
  })

  return isSameDay(rounded, dayWithCurrentTime)
    ? rounded
    : subMinutes(rounded, nearestTo)
}

/**
 * @param {number} hours
 * @param {number} minutes
 * @param {number} seconds
 * @param {number} milliseconds
 * @return {Date}
 */
export function createTime({
  hours = DAY_FIRST_HOUR,
  milliseconds = SECOND_FIRST_MILLISECOND,
  minutes = HOUR_FIRST_MINUTE,
  seconds = MINUTE_FIRST_SECOND,
} = EMPTY_OBJECT) {
  return set(createDate('UTC', 0), { hours, milliseconds, minutes, seconds })
}

/**
 * @param {Date} date
 *
 * @returns {Date}
 */
export function createTimeFrom(date) {
  return createTime({
    hours: getHours(date),
    milliseconds: getMilliseconds(date),
    minutes: getMinutes(date),
    seconds: getSeconds(date),
  })
}

/**
 * @param {string} string
 *
 * @return {{ day: number, month: number, year: number } | null}
 */
export function matchDateInput(string) {
  const match = string.match(DATE_INPUT_REGEXP)

  return match &&
    isValid(
      new Date(
        [
          padStart(match[3], 4, '0'),
          padStart(match[2], 2, '0'),
          padStart(match[1], 2, '0'),
        ].join('-')
      )
    )
    ? {
        day: parseInt(match[1], 10),
        month: parseInt(match[2], 10) - 1,
        year: parseInt(match[3], 10),
      }
    : null
}

/**
 * @param {string} string
 * @return {{ hours: number, minutes: number } | null}
 */
export function matchTimeInput(string) {
  const match = string.match(TIME_INPUT_REGEXP)

  return match &&
    isValid(
      new Date(
        `1900-01-01T${padStart(match[1], 2, '0')}:${padStart(match[2], 2, '0')}`
      )
    )
    ? {
        hours: parseInt(match[1], 10),
        minutes: parseInt(match[2], 10),
      }
    : null
}
